import { NumberInput, Tooltip } from "@mantine/core";
import { t } from "i18next";
import type { MRT_ColumnDef } from "mantine-react-table-open";
import { useState } from "react";
import { setErrorsFromResponseData } from "../../../../components/BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import { HeaderWithHelpIcon } from "../../../../mantine/mrt/components/header/HeaderWithHelpIcon";
import { useToast } from "../../../../mantine/notifications/useToast";
import type {
  ExpressOnboardingGenerator,
  GetErrorFn,
  SetErrorFn
} from "../../ExpressOnboarding.types";
import { useExpressOnboardingMutations } from "../hooks/useExpressOnboardingMutations";

function getExpressOnboardingTableAZWColumn(
  getError: GetErrorFn,
  setError: SetErrorFn
): MRT_ColumnDef<ExpressOnboardingGenerator> {
  return {
    id: "anzulegender_wert",
    accessorKey: "anzulegender_wert",
    header: "Anzulegender Wert",
    Header: () => (
      <HeaderWithHelpIcon
        headerText="Anzulegender Wert"
        helpText="Wenn Sie mehrere zeitveränderliche Werte eintragen wollen, 
        nutzen Sie bitte die Eingabemaske in der Liegenschaft unter „Erzeuger”."
      />
    ),
    enableGlobalFilter: false,
    Cell: ({ cell, row }) => {
      const value =
        cell.getValue<ExpressOnboardingGenerator["anzulegender_wert"]>();

      const hasEegInbetriebnahme =
        !!row.getValue<ExpressOnboardingGenerator["eeg_inbetriebnahme"]>(
          "eeg_inbetriebnahme"
        );

      const [currentValue, setCurrentValue] =
        useState<ExpressOnboardingGenerator["anzulegender_wert"]>(value);

      const { expressOnboardingGeneratorSave, pendingGeneratorIds } =
        useExpressOnboardingMutations();

      const { showErrorToast } = useToast();

      const rowIsSaving = pendingGeneratorIds.has(row.original.id);

      const cantEditReason = row.original.uneditable_fields?.anzulegender_wert;

      return (
        <Tooltip
          disabled={!cantEditReason && hasEegInbetriebnahme}
          label={cantEditReason ?? "EEG-Inbetriebnahme erforderlich"}
        >
          <NumberInput
            disabled={!!cantEditReason || !hasEegInbetriebnahme || rowIsSaving}
            error={getError(row.original.id, "anzulegender_wert")}
            loading={rowIsSaving}
            suffix=" ct/kWh"
            value={currentValue ?? ""}
            onBlur={() => {
              const hasChange = currentValue !== value;
              if (!hasChange) {
                return;
              }

              expressOnboardingGeneratorSave
                .mutateAsync({
                  id: row.original.id,
                  anzulegender_wert: currentValue ?? null
                })
                .catch((error) => {
                  setErrorsFromResponseData(
                    showErrorToast,
                    error,
                    row.original,
                    (field: string, err: { message?: string }) =>
                      setError(row.original.id, {
                        [field]: err?.message ?? t("errors.UnknownError")
                      }),
                    t("errors.UnknownError")
                  );
                });
            }}
            onChange={(newValue) => {
              const numericNewValue =
                typeof newValue === "number"
                  ? newValue
                  : newValue === ""
                    ? null
                    : parseFloat(newValue);

              setCurrentValue(numericNewValue);
            }}
          />
        </Tooltip>
      );
    }
  };
}

export { getExpressOnboardingTableAZWColumn };
